import api from "../../utils/api";
import type { OrganizationRole } from "common/constants";
import type {
  AvailableOrganizationMember,
  ProjectMemberAssignment,
  ProjectTeamChanges,
  ProjectTeamData,
  ProjectTeamMember,
} from "./types";

export const fetchProjectTeamData = async (
  projectId: number,
): Promise<ProjectTeamData> => {
  const [team, available] = await Promise.all([
    api.fetchProjectTeam(projectId),
    api.fetchAvailableMembers(projectId),
  ]);
  const availableMembers: AvailableOrganizationMember[] = (available ?? []).map(
    (member: {
      id: number;
      name: string;
      email: string;
      organizationRole: OrganizationRole;
    }) => ({
      id: member.id,
      name: member.name,
      email: member.email,
      projectRole: "member",
      organizationRole: member.organizationRole,
    }),
  );

  return {
    teamMembers: team.teamMembers ?? [],
    availableMembers,
  };
};

const toAssignment = ({
  id,
  projectRole,
}: ProjectTeamMember): ProjectMemberAssignment => ({
  userId: id,
  role: projectRole,
});

// Diffs the staged team against the server snapshot, or null when unchanged.
export const diffTeam = (
  original: ProjectTeamMember[],
  current: ProjectTeamMember[],
): ProjectTeamChanges | null => {
  const originalById = new Map(original.map((member) => [member.id, member]));

  const addedMembers: ProjectMemberAssignment[] = [];
  const updatedMembers: ProjectMemberAssignment[] = [];
  const currentIds = new Set<number>();

  for (const member of current) {
    currentIds.add(member.id);

    const previous = originalById.get(member.id);

    if (!previous) {
      addedMembers.push(toAssignment(member));
    } else if (previous.projectRole !== member.projectRole) {
      updatedMembers.push(toAssignment(member));
    }
  }

  const removedMemberIds = original
    .filter((member) => !currentIds.has(member.id))
    .map((member) => member.id);

  return addedMembers.length || updatedMembers.length || removedMemberIds.length
    ? { addedMembers, updatedMembers, removedMemberIds }
    : null;
};
